import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Avatar,
  IconButton,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EventIcon from "@mui/icons-material/Event";
import FavoriteIcon from "@mui/icons-material/Favorite";
import HomeIcon from "@mui/icons-material/Home";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BadgeIcon from "@mui/icons-material/Badge";
import "./styles-employees.css";
import "./styles.css";
import EmploymentAgreementGenerator from "./EmploymentAgreementGenerator";

const PersonDetailDialog = ({ selectedPerson, open, onClose, onEdit }) => {
  const [showAgreement, setShowAgreement] = useState(false);

  if (!selectedPerson) return null;

  const handleOpenAgreement = () => {
    setShowAgreement(true);
  };

  const handleCloseAgreement = () => {
    setShowAgreement(false);
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(selectedPerson);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #228B22 0%, #1b6b1b 100%)",
          color: "#fff",
          padding: "20px 24px",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              alt={selectedPerson.name}
              src={selectedPerson.profile_image}
              sx={{
                width: 60,
                height: 60,
                border: "3px solid white",
                fontSize: "1.5rem",
                boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
              }}
            >
              {selectedPerson.name?.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                {selectedPerson.name}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                {selectedPerson.role_name || "Employee"} • {selectedPerson.staff_id || selectedPerson.uId}
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.15)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.25)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "24px",
          backgroundColor: "#f8f9fa",
        }}
        className="employee-modal-content"
      >
        <Grid container spacing={3}>
          {/* Basic Information Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <PersonIcon sx={{ color: "#228B22" }} />
                  <Typography variant="h6" sx={{ fontWeight: "bold", color: "#228B22" }}>
                    Basic Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <InfoRow icon={<BadgeIcon fontSize="small" />} label="Staff ID" value={selectedPerson.staff_id || selectedPerson.uId || "Not assigned"} highlight />
                  <InfoRow icon={<EmailIcon fontSize="small" />} label="User ID" value={selectedPerson.login_id || "Not assigned"} />
                  <InfoRow label="Gender" value={selectedPerson.gender || "Not specified"} />
                  <InfoRow label="Date of Birth" value={selectedPerson.dateOfBirth ? new Date(selectedPerson.dateOfBirth).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }).replace(/,/g, "") : "Not specified"} />
                  <InfoRow label="Marital Status" value={selectedPerson.maritalStatus || "Not specified"} />
                  <InfoRow label="Blood Group" value={selectedPerson.bloodGroup || "Not specified"} />
                  <InfoRow label="Nationality" value={selectedPerson.res_country !== "NONE" ? selectedPerson.res_country : "Not specified"} />
                  <InfoRow label="Govt ID" value={selectedPerson.governmentId || "Not specified"} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Information Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <PhoneIcon sx={{ color: "#228B22" }} />
                  <Typography variant="h6" sx={{ fontWeight: "bold", color: "#228B22" }}>
                    Contact Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <InfoRow 
                    icon={<EmailIcon fontSize="small" />} 
                    label="Email" 
                    value={
                      <a
                        href={`mailto:${selectedPerson.per_email !== "NONE" ? selectedPerson.per_email : selectedPerson.login_id}`}
                        style={{ color: "#228B22", textDecoration: "none", fontWeight: 500 }}
                      >
                        {selectedPerson.per_email !== "NONE" ? selectedPerson.per_email : selectedPerson.login_id}
                      </a>
                    }
                  />
                  <InfoRow 
                    icon={<PhoneIcon fontSize="small" />} 
                    label="Contact" 
                    value={selectedPerson.contactNumber !== "NONE" ? selectedPerson.contactNumber : "Not specified"} 
                  />
                  <InfoRow 
                    icon={<HomeIcon fontSize="small" />} 
                    label="Current Address" 
                    value={selectedPerson.currentAddress !== "NONE" ? selectedPerson.currentAddress : "Not specified"} 
                  />
                  <InfoRow 
                    icon={<HomeIcon fontSize="small" />} 
                    label="Permanent Address" 
                    value={selectedPerson.permanentAddress !== "NONE" ? selectedPerson.permanentAddress : "Not specified"} 
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Employment Information Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <WorkIcon sx={{ color: "#228B22" }} />
                  <Typography variant="h6" sx={{ fontWeight: "bold", color: "#228B22" }}>
                    Employment Details
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <InfoRow label="Department" value={selectedPerson.dep_name !== "NONE" ? selectedPerson.dep_name : "Not assigned"} highlight />
                  <InfoRow label="Department ID" value={selectedPerson.dep_id || "Not assigned"} small />
                  <InfoRow label="Role" value={selectedPerson.role_name !== "NONE" ? selectedPerson.role_name : "Not assigned"} highlight />
                  <InfoRow label="Role ID" value={selectedPerson.roleId || "Not assigned"} small />
                  <InfoRow label="Designation" value={selectedPerson.desName || "Not assigned"} />
                  <InfoRow label="Level" value={selectedPerson.level !== "NONE" && selectedPerson.level !== "None" ? selectedPerson.level : "Not assigned"} />
                  <InfoRow label="Grade" value={selectedPerson.grade_id || selectedPerson.grade || "Unassigned"} />
                  <InfoRow label="Employment Type" value={selectedPerson.empTypeName || "Not specified"} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Work Details Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <EventIcon sx={{ color: "#228B22" }} />
                  <Typography variant="h6" sx={{ fontWeight: "bold", color: "#228B22" }}>
                    Work Details
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <InfoRow 
                    icon={<EventIcon fontSize="small" />} 
                    label="Joining Date" 
                    value={selectedPerson.joiningDate !== "NONE" ? selectedPerson.joiningDate : "Not specified"} 
                  />
                  <InfoRow 
                    icon={<LocationOnIcon fontSize="small" />} 
                    label="Work Location" 
                    value={selectedPerson.workLocation !== "NONE" ? selectedPerson.workLocation : "Not specified"} 
                  />
                  <InfoRow label="Probation Period" value={selectedPerson.probationPeriod || "Not specified"} />
                  <InfoRow label="Reporting Manager" value={selectedPerson.reportingManagerName || "Not assigned"} />
                  <InfoRow label="Manager ID" value={selectedPerson.reportingMngId !== "NONE" ? selectedPerson.reportingMngId : "Not assigned"} small />
                  <InfoRow 
                    label="Created At" 
                    value={selectedPerson.createdAt ? new Date(selectedPerson.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "Not available"} 
                    small
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Leave Allocation Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <EventIcon sx={{ color: "#228B22" }} />
                  <Typography variant="h6" sx={{ fontWeight: "bold", color: "#228B22" }}>
                    Leave Allocation
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <LeaveCard 
                      title="Annual Leave" 
                      used={selectedPerson.usedAnnualLeave || 0} 
                      allocated={selectedPerson.allocatedAnnualLeave || 0}
                      color="#1976d2"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <LeaveCard 
                      title="Sick Leave" 
                      used={selectedPerson.usedSickLeave || 0} 
                      allocated={selectedPerson.allocatedSickLeave || 0}
                      color="#ed6c02"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <LeaveCard 
                      title="Emergency Leave" 
                      used={selectedPerson.usedEmergencyLeave || 0} 
                      allocated={selectedPerson.allocatedEmergencyLeave || 0}
                      color="#d32f2f"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <LeaveCard 
                      title="Unpaid Leave" 
                      used={0} 
                      allocated={selectedPerson.allocatedUnpaidLeave || 0}
                      color="#757575"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Personal Information Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <FavoriteIcon sx={{ color: "#228B22" }} />
                  <Typography variant="h6" sx={{ fontWeight: "bold", color: "#228B22" }}>
                    Personal Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      <InfoRow label="Hobbies" value={selectedPerson.hobbies !== "{{Hobbies}}" && selectedPerson.hobbies !== "NONE" ? selectedPerson.hobbies : "Not specified"} />
                      <InfoRow label="Passion" value={selectedPerson.passion !== "NONE" ? selectedPerson.passion : "Not specified"} />
                      <InfoRow label="Short Term Goal" value={selectedPerson.shortTermGoal || "Not specified"} />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      <InfoRow label="Long Term Goal" value={selectedPerson.longTermGoal || "Not specified"} />
                      <InfoRow label="Primary Language" value={selectedPerson.primaryLanguage || "Not specified"} />
                      <InfoRow label="Known Languages" value={selectedPerson.knownLanguage || "Not specified"} />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px", backgroundColor: "#f8f9fa", borderTop: "1px solid #e0e0e0" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: "#666",
            borderColor: "#ddd",
            "&:hover": {
              borderColor: "#bbb",
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          Close
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleOpenAgreement}
          variant="contained"
          startIcon={<DescriptionIcon />}
          sx={{
            backgroundColor: "#1976d2",
            color: "#fff",
            mr: 1,
            "&:hover": {
              backgroundColor: "#1565c0",
            },
          }}
        >
          Generate Agreement
        </Button>
        <Button
          onClick={handleEdit}
          variant="contained"
          startIcon={<EditIcon />}
          sx={{
            background: "linear-gradient(135deg, #228B22 0%, #1b6b1b 100%)",
            color: "#fff",
            "&:hover": {
              background: "linear-gradient(135deg, #1b6b1b 0%, #145014 100%)",
            },
          }}
        >
          Edit Person
        </Button>
      </DialogActions>

      {/* Employment Agreement Generator */}
      <EmploymentAgreementGenerator
        open={showAgreement}
        onClose={handleCloseAgreement}
        personData={selectedPerson}
      />
    </Dialog>
  );
};

// Helper component for info rows
const InfoRow = ({ icon, label, value, highlight, small }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
    {icon && <Box sx={{ color: "#666", mt: 0.3 }}>{icon}</Box>}
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="body2"
        sx={{
          color: "#666",
          fontSize: small ? "0.75rem" : "0.875rem",
          mb: 0.3,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: highlight ? "#228B22" : "#333",
          fontWeight: highlight ? 600 : 500,
          fontSize: small ? "0.875rem" : "1rem",
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

// Helper component for leave cards
const LeaveCard = ({ title, used, allocated, color }) => {
  const remaining = allocated - used;
  const percentage = allocated > 0 ? (used / allocated) * 100 : 0;
  
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "8px",
        border: `2px solid ${color}20`,
        backgroundColor: `${color}05`,
        textAlign: "center",
      }}
    >
      <Typography variant="body2" sx={{ color: "#666", mb: 1, fontWeight: 500 }}>
        {title}
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "baseline", gap: 0.5, mb: 1 }}>
        <Typography variant="h4" sx={{ color: color, fontWeight: "bold" }}>
          {remaining}
        </Typography>
        <Typography variant="body2" sx={{ color: "#666" }}>
          / {allocated}
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ color: "#999" }}>
        Used: {used} day{used !== 1 ? 's' : ''}
      </Typography>
      <Box
        sx={{
          mt: 1.5,
          height: 6,
          backgroundColor: "#e0e0e0",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${percentage}%`,
            height: "100%",
            backgroundColor: color,
            transition: "width 0.3s ease",
          }}
        />
      </Box>
    </Box>
  );
};


export default PersonDetailDialog;